import { FetchError } from "@medusajs/js-sdk"
import { CreateOrderCreditLineDTO, HttpTypes } from "@medusajs/types"
import { PayoutDTO } from "../../types/payout/common"
import {
  QueryKey,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query"
import { sdk } from "../../lib/client"
import { queryClient } from "../../lib/query-client"
import { queryKeysFactory, TQueryKey } from "../../lib/query-key-factory"
import { inventoryItemsQueryKeys } from "./inventory"
import { reservationItemsQueryKeys } from "./reservations"

const ORDERS_QUERY_KEY = "orders" as const
const _orderKeys = queryKeysFactory(ORDERS_QUERY_KEY) as TQueryKey<"orders"> & {
  preview: (orderId: string) => any
  changes: (orderId: string) => any
  lineItems: (orderId: string) => any
  shippingOptions: (orderId: string) => any
}

_orderKeys.preview = function (id: string) {
  return [this.detail(id), "preview"]
}

_orderKeys.changes = function (id: string) {
  return [this.detail(id), "changes"]
}

_orderKeys.lineItems = function (id: string) {
  return [this.detail(id), "lineItems"]
}

_orderKeys.shippingOptions = function (id: string) {
  return [this.detail(id), "shippingOptions"]
}

export const ordersQueryKeys = _orderKeys

export const useOrder = (
  id: string,
  query?: Record<string, any>,
  options?: Omit<
    UseQueryOptions<any, FetchError, any, QueryKey>,
    "queryFn" | "queryKey"
  >
) => {
  const { data, ...rest } = useQuery({
    queryFn: async () => sdk.admin.order.retrieve(id, query),
    queryKey: ordersQueryKeys.detail(id, query),
    ...options,
  })

  return { ...data, ...rest }
}

export const useUpdateOrder = (
  id: string,
  options?: UseMutationOptions<
    HttpTypes.AdminOrderResponse,
    FetchError,
    HttpTypes.AdminUpdateOrder
  >
) => {
  return useMutation({
    mutationFn: (payload: HttpTypes.AdminUpdateOrder) =>
      sdk.admin.order.update(id, payload),
    onSuccess: (data: any, variables: any, context: any, _ctx: any) => {
      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.detail(id),
      })

      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.changes(id),
      })

      // TODO: enable when needed
      // queryClient.invalidateQueries({
      //   queryKey: ordersQueryKeys.lists(),
      // })

      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const useOrderPreview = (
  id: string,
  query?: HttpTypes.AdminOrderFilters,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminOrderPreviewResponse,
      FetchError,
      HttpTypes.AdminOrderPreviewResponse,
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const { data, ...rest } = useQuery({
    queryFn: async () => sdk.admin.order.retrievePreview(id, query),
    queryKey: ordersQueryKeys.preview(id),
    ...options,
  })

  return { ...data, ...rest }
}

export const useOrders = (
  query?: HttpTypes.AdminOrderFilters,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminOrderListResponse,
      FetchError,
      HttpTypes.AdminOrderListResponse,
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const { data, ...rest } = useQuery({
    queryFn: async () => sdk.admin.order.list(query),
    queryKey: ordersQueryKeys.list(query),
    ...options,
  })

  return { ...data, ...rest }
}

export const useOrderShippingOptions = (
  id: string,
  query?: HttpTypes.AdminGetOrderShippingOptionList,
  options?: Omit<
    UseQueryOptions<
      { shipping_options: HttpTypes.AdminShippingOption[] },
      FetchError,
      { shipping_options: HttpTypes.AdminShippingOption[] },
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const { data, ...rest } = useQuery({
    queryFn: async () => sdk.admin.order.listShippingOptions(id, query),
    queryKey: ordersQueryKeys.shippingOptions(id),
    ...options,
  })

  return { ...data, ...rest }
}

export const useOrderChanges = (
  id: string,
  query?: HttpTypes.AdminOrderChangesFilters,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminOrderChangesResponse,
      FetchError,
      HttpTypes.AdminOrderChangesResponse,
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const { data, ...rest } = useQuery({
    queryFn: async () => sdk.admin.order.listChanges(id, query),
    queryKey: ordersQueryKeys.changes(id),
    ...options,
  })

  return { ...data, ...rest }
}

export const useOrderLineItems = (
  id: string,
  query?: HttpTypes.AdminOrderItemsFilters,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminOrderLineItemsListResponse,
      FetchError,
      HttpTypes.AdminOrderLineItemsListResponse,
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const { data, ...rest } = useQuery({
    queryFn: async () => sdk.admin.order.listLineItems(id, query),
    queryKey: ordersQueryKeys.lineItems(id),
    ...options,
  })

  return { ...data, ...rest }
}

export const useCreateOrderFulfillment = (
  orderId: string,
  options?: UseMutationOptions<
    HttpTypes.AdminOrderResponse,
    FetchError,
    HttpTypes.AdminCreateOrderFulfillment
  >
) => {
  return useMutation({
    mutationFn: (payload: HttpTypes.AdminCreateOrderFulfillment) =>
      sdk.admin.order.createFulfillment(orderId, payload),
    onSuccess: (data: any, variables: any, context: any, _ctx: any) => {
      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.all,
      })

      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.preview(orderId),
      })

      queryClient.invalidateQueries({
        queryKey: reservationItemsQueryKeys.lists(),
      })

      queryClient.invalidateQueries({
        queryKey: inventoryItemsQueryKeys.details(),
      })

      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const useCancelOrderFulfillment = (
  orderId: string,
  fulfillmentId: string,
  options?: UseMutationOptions<any, FetchError, any>
) => {
  return useMutation({
    mutationFn: (payload: { no_notification?: boolean }) =>
      sdk.admin.order.cancelFulfillment(orderId, fulfillmentId, payload),
    onSuccess: (data: any, variables: any, context: any, _ctx: any) => {
      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.all,
      })

      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.preview(orderId),
      })

      queryClient.invalidateQueries({
        queryKey: reservationItemsQueryKeys.lists(),
      })

      queryClient.invalidateQueries({
        queryKey: inventoryItemsQueryKeys.details(),
      })

      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const useCreateOrderShipment = (
  orderId: string,
  fulfillmentId: string,
  options?: UseMutationOptions<
    { order: HttpTypes.AdminOrder },
    FetchError,
    HttpTypes.AdminCreateOrderShipment
  >
) => {
  return useMutation({
    mutationFn: (payload: HttpTypes.AdminCreateOrderShipment) =>
      sdk.admin.order.createShipment(orderId, fulfillmentId, payload),
    onSuccess: (data: any, variables: any, context: any, _ctx: any) => {
      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.all,
      })

      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.preview(orderId),
      })

      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const useMarkOrderFulfillmentAsDelivered = (
  orderId: string,
  fulfillmentId: string,
  options?: UseMutationOptions<
    { order: HttpTypes.AdminOrder },
    FetchError,
    void
  >
) => {
  return useMutation({
    mutationFn: () => sdk.admin.order.markAsDelivered(orderId, fulfillmentId),
    onSuccess: (data: any, variables: any, context: any, _ctx: any) => {
      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.all,
      })

      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.preview(orderId),
      })

      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const useCancelOrderWithFulfillments = (
  orderId: string,
  options?: UseMutationOptions<HttpTypes.AdminOrderResponse, FetchError, void>
) => {
  return useMutation<HttpTypes.AdminOrderResponse, FetchError, void>({
    mutationFn: () =>
      sdk.client.fetch<HttpTypes.AdminOrderResponse>(
        `/admin/orders/${orderId}/cancel-with-fulfillments`,
        {
          method: "POST",
        }
      ),
    onSuccess: (data: any, variables: any, context: any, _ctx: any) => {
      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.detail(orderId),
      })

      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.preview(orderId),
      })

      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const useOrderPayoutStatus = (
  orderId: string,
  options?: Omit<
    UseQueryOptions<
      { payout: PayoutDTO | null; seller_has_payout_account: boolean },
      FetchError,
      { payout: PayoutDTO | null; seller_has_payout_account: boolean },
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const { data, ...rest } = useQuery({
    queryFn: async () =>
      sdk.client.fetch<{ payout: PayoutDTO | null; seller_has_payout_account: boolean }>(
        `/admin/orders/${orderId}/payout`,
        { method: "GET" }
      ),
    queryKey: [...ordersQueryKeys.detail(orderId), "payout"],
    ...options,
  })

  return {
    payout: data?.payout ?? null,
    sellerHasPayoutAccount: data?.seller_has_payout_account ?? false,
    ...rest,
  }
}

export const usePayoutOrder = (
  orderId: string,
  options?: UseMutationOptions<void, FetchError, void>
) => {
  return useMutation<void, FetchError, void>({
    mutationFn: () =>
      sdk.client.fetch<void>(`/admin/orders/${orderId}/payout`, {
        method: "POST",
      }),
    onSuccess: (data: any, variables: any, context: any, _ctx: any) => {
      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.detail(orderId),
      })

      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.preview(orderId),
      })

      queryClient.invalidateQueries({
        queryKey: [...ordersQueryKeys.detail(orderId), "payout"],
      })

      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export type NotificationHistoryEntry = {
  id: string
  sent_at: string
  sent_to: string | null
}

export type NotificationByRecipient = {
  recipient: string | null
  count: number
  last_sent_at: string
}

export type NotificationHistoryResponse = {
  order_id: string
  sent_count: number
  last_sent_at: string | null
  last_sent_to: string | null
  by_recipient: NotificationByRecipient[]
  history: NotificationHistoryEntry[]
}

export const useNotificationHistory = (orderId: string, enabled: boolean) => {
  return useQuery<NotificationHistoryResponse, FetchError>({
    queryKey: [...ordersQueryKeys.detail(orderId), "notification-history"],
    queryFn: () =>
      sdk.client.fetch(`/admin/orders/${orderId}/resend-notification`, {
        method: "GET",
      }),
    enabled,
  })
}

export type ResendNotificationRecipient = "booking_email" | "customer_email"

export type ResendNotificationPayload = {
  type: "buyer_confirmation"
  recipient?: ResendNotificationRecipient
}

export type ResendNotificationResponse = {
  success: boolean
  sent_to: string
  recipient: ResendNotificationRecipient
  template: string
}

export const useResendOrderNotification = (
  orderId: string,
  options?: UseMutationOptions<
    ResendNotificationResponse,
    FetchError,
    ResendNotificationPayload
  >
) => {
  return useMutation({
    mutationFn: (payload: ResendNotificationPayload) =>
      sdk.client.fetch<ResendNotificationResponse>(
        `/admin/orders/${orderId}/resend-notification`,
        { method: "POST", body: payload }
      ),
    onSuccess: (data: any, variables: any, context: any, _ctx: any) => {
      queryClient.invalidateQueries({
        queryKey: [...ordersQueryKeys.detail(orderId), "notification-history"],
      })
      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const useRecreateCommission = (
  orderId: string,
  options?: UseMutationOptions<void, FetchError, void>
) => {
  return useMutation<void, FetchError, void>({
    mutationFn: () =>
      sdk.client.fetch<void>(`/admin/orders/${orderId}/recreate-commission`, {
        method: "POST",
      }),
    onSuccess: (data: any, variables: any, context: any, _ctx: any) => {
      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.detail(orderId),
      })
      queryClient.invalidateQueries({
        queryKey: [...ordersQueryKeys.detail(orderId), "commission-lines"],
      })
      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const usePartialPayout = (
  orderId: string,
  options?: UseMutationOptions<void, FetchError, { amount: number }>
) => {
  return useMutation<void, FetchError, { amount: number }>({
    mutationFn: (payload: { amount: number }) =>
      sdk.client.fetch<void>(`/admin/orders/${orderId}/partial-payout`, {
        method: "POST",
        body: payload,
      }),
    onSuccess: (data: any, variables: any, context: any, _ctx: any) => {
      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.detail(orderId),
      })
      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.preview(orderId),
      })
      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const useRequestTransferOrder = (
  orderId: string,
  options?: UseMutationOptions<
    HttpTypes.AdminOrderResponse,
    FetchError,
    HttpTypes.AdminRequestOrderTransfer
  >
) => {
  return useMutation({
    mutationFn: (payload: HttpTypes.AdminRequestOrderTransfer) =>
      sdk.admin.order.requestTransfer(orderId, payload),
    onSuccess: (data: any, variables: any, context: any, _ctx: any) => {
      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.preview(orderId),
      })

      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.changes(orderId),
      })

      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const useCancelOrderTransfer = (
  orderId: string,
  options?: UseMutationOptions<any, FetchError, void>
) => {
  return useMutation({
    mutationFn: () => sdk.admin.order.cancelTransfer(orderId),
    onSuccess: (data: any, variables: any, context: any, _ctx: any) => {
      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.preview(orderId),
      })

      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.changes(orderId),
      })

      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const usePayoutReversal = (
  orderId: string,
  options?: UseMutationOptions<void, FetchError, void>
) => {
  return useMutation<void, FetchError, void>({
    mutationFn: () =>
      sdk.client.fetch<void>(`/admin/orders/${orderId}/payout-reversal`, {
        method: "POST",
      }),
    onSuccess: (data: any, variables: any, context: any, _ctx: any) => {
      queryClient.invalidateQueries({ queryKey: ordersQueryKeys.detail(orderId) })
      queryClient.invalidateQueries({ queryKey: [...ordersQueryKeys.detail(orderId), "payout"] })
      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const useTriggerFulfillment = (
  orderId: string,
  options?: UseMutationOptions<void, FetchError, void>
) => {
  return useMutation<void, FetchError, void>({
    mutationFn: () =>
      sdk.client.fetch<void>(`/admin/orders/${orderId}/trigger-fulfillment`, {
        method: "POST",
      }),
    onSuccess: (data: any, variables: any, context: any, _ctx: any) => {
      queryClient.invalidateQueries({ queryKey: ordersQueryKeys.detail(orderId) })
      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const useTransferGuestBooking = (
  options?: UseMutationOptions<void, FetchError, { orderSetId: string }>
) => {
  return useMutation<void, FetchError, { orderSetId: string }>({
    mutationFn: (payload: { orderSetId: string }) =>
      sdk.client.fetch<void>("/admin/order-sets/transfer", {
        method: "POST",
        body: payload,
      }),
    ...options,
  })
}

export const useCreateOrderCreditLine = (
  orderId: string,
  options?: UseMutationOptions<
    HttpTypes.AdminOrderResponse,
    FetchError,
    Omit<CreateOrderCreditLineDTO, "order_id">
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.admin.order.createCreditLine(orderId, payload),
    onSuccess: (data, variables, context, _ctx) => {
      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.details(),
      })

      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.preview(orderId),
      })

      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}
